import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import AppIcon from '../components/common/AppIcon/AppIcon';

const ToastContext = createContext();

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  // Confirm dialog state
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'danger', // 'danger' or 'warning'
  });

  const removeToast = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);

    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const toast = useMemo(() => ({
    success: (msg, duration) => addToast(msg, 'success', duration),
    error: (msg, duration) => addToast(msg, 'error', duration ?? 6000),
    warning: (msg, duration) => addToast(msg, 'warning', duration),
    info: (msg, duration) => addToast(msg, 'info', duration),
  }), [addToast]);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        variant: options.variant || 'danger',
        onConfirm: () => {
          setConfirmState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirmState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        },
      });
    });
  }, []);

  return (
    <ToastContext.Provider value={{ toast, removeToast, confirm }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <ConfirmDialog {...confirmState} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div style={containerStyles}>
      {toasts.map(t => (
        <Toast key={t.id} toast={t} onRemove={onRemove} />
      ))}
    </div>
  );
};

const Toast = ({ toast, onRemove }) => {
  const [exiting, setExiting] = React.useState(false);

  const handleClose = () => {
    setExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const config = typeConfig[toast.type] || typeConfig.info;

  return (
    <div
      style={{
        ...toastStyles,
        background: config.bg,
        borderLeft: `4px solid ${config.accent}`,
        animation: exiting ? 'toastSlideOut 0.3s ease forwards' : 'toastSlideIn 0.35s ease',
      }}
      role="alert"
    >
      <span style={iconStyles}>
        <AppIcon name={config.icon} size={18} />
      </span>
      <span style={messageStyles}>{toast.message}</span>
      <button
        onClick={handleClose}
        style={closeBtnStyles}
        aria-label="Close notification"
      >
        ✕
      </button>
    </div>
  );
};

/* ── Confirm Dialog ── */
const ConfirmDialog = ({ isOpen, message, onConfirm, onCancel, confirmText, cancelText, variant }) => {
  if (!isOpen) return null;

  const accentColor = variant === 'danger' ? '#EF4444' : '#F59E0B';

  return (
    <div style={overlayStyles} onClick={onCancel}>
      <div style={dialogStyles} onClick={e => e.stopPropagation()}>
        <div style={dialogIconWrap}>
          <AppIcon name="disputes" size={34} />
        </div>
        <p style={dialogMessageStyles}>{message}</p>
        <div style={dialogActionsStyles}>
          <button onClick={onCancel} style={dialogCancelBtnStyles}>
            {cancelText}
          </button>
          <button onClick={onConfirm} style={{ ...dialogConfirmBtnStyles, background: accentColor }}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const typeConfig = {
  success: { icon: 'approvals', accent: '#4CAF50', bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' },
  error:   { icon: 'disputes', accent: '#EF4444', bg: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)' },
  warning: { icon: 'disputes', accent: '#F59E0B', bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' },
  info:    { icon: 'info', accent: '#3B82F6', bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' },
};

/* ── Toast Styles ── */
const containerStyles = {
  position: 'fixed',
  top: '24px',
  right: '24px',
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  maxWidth: '440px',
  width: '100%',
  pointerEvents: 'none',
};

const toastStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 18px',
  borderRadius: '12px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
  backdropFilter: 'blur(8px)',
  pointerEvents: 'auto',
  minWidth: '320px',
  fontSize: '14px',
  lineHeight: '1.5',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const iconStyles = {
  fontSize: '18px',
  flexShrink: 0,
};

const messageStyles = {
  flex: 1,
  color: '#1f2937',
  fontWeight: 500,
  wordBreak: 'break-word',
};

const closeBtnStyles = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#9ca3af',
  padding: '4px',
  lineHeight: 1,
  flexShrink: 0,
  transition: 'color 0.2s',
  borderRadius: '4px',
};

/* ── Confirm Dialog Styles ── */
const overlayStyles = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10001,
  animation: 'toastSlideIn 0.2s ease',
};

const dialogStyles = {
  background: 'white',
  borderRadius: '16px',
  padding: '32px',
  maxWidth: '420px',
  width: '90%',
  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  textAlign: 'center',
};

const dialogIconWrap = {
  marginBottom: '16px',
};

const dialogMessageStyles = {
  fontSize: '15px',
  color: '#374151',
  lineHeight: '1.6',
  margin: '0 0 24px 0',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const dialogActionsStyles = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'center',
};

const dialogCancelBtnStyles = {
  padding: '10px 24px',
  borderRadius: '10px',
  border: '2px solid #e5e7eb',
  background: 'white',
  color: '#374151',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s',
  fontFamily: 'inherit',
};

const dialogConfirmBtnStyles = {
  padding: '10px 24px',
  borderRadius: '10px',
  border: 'none',
  color: 'white',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s',
  fontFamily: 'inherit',
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context.toast;
};

export const useConfirm = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ToastProvider');
  }
  return context.confirm;
};

export default ToastContext;
